import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Bell, Briefcase, CalendarDays, CheckCircle2, FileText, Lock,
  Menu, MousePointerClick, Scale, Search, Trash2, UploadCloud, Users, X,
} from "lucide-react";
import { toast } from "sonner";
import { StatusBadge, EmptyState, PaginationBar } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

const SECTIONS = [
  { id: "tokens", num: "01", label: "Design System" },
  { id: "components", num: "02", label: "Components" },
  { id: "public-site", num: "03", label: "Public Website" },
  { id: "responsive-site", num: "04", label: "Responsive Website" },
  { id: "dashboard", num: "05", label: "Dashboard" },
  { id: "clients", num: "06", label: "Clients" },
  { id: "client-detail", num: "07", label: "Client Details" },
  { id: "documents", num: "08", label: "Documents" },
  { id: "cases", num: "09", label: "Cases" },
  { id: "appointments", num: "10", label: "Appointments" },
  { id: "blog", num: "11", label: "Blog" },
  { id: "profile-settings", num: "12", label: "Profile & Settings" },
  { id: "responsive-dash", num: "13", label: "Responsive Dashboard" },
  { id: "flows", num: "14", label: "User Flows" },
];

const COLORS = [
  { name: "Navy / Primary", hex: "#0A1128", token: "navy", text: "text-ivory", usage: "Primary surfaces, hero, sidebar, footer, headings" },
  { name: "Navy 800", hex: "#1C2541", token: "navy-800", text: "text-ivory", usage: "Hover states, secondary dark surfaces" },
  { name: "Gold / Accent", hex: "#D4AF37", token: "gold", text: "text-navy", usage: "Primary CTA, active states, dividers, focus rings" },
  { name: "Gold 600", hex: "#C5A059", token: "gold-600", text: "text-navy", usage: "CTA hover" },
  { name: "Gold 100", hex: "#F5EBD0", token: "gold-100", text: "text-navy", usage: "Icon wells, highlights, banners" },
  { name: "Ivory / Background", hex: "#FDFBF7", token: "ivory", text: "text-navy", usage: "Page background (public site)" },
  { name: "Surface / White", hex: "#FFFFFF", token: "card", text: "text-navy", usage: "Cards, tables, modals" },
  { name: "Muted", hex: "#F3F4F6", token: "muted", text: "text-navy", usage: "Skeletons, subtle fills, hover rows" },
  { name: "Muted Foreground", hex: "#4B5563", token: "muted-foreground", text: "text-ivory", usage: "Secondary text, descriptions" },
  { name: "Border", hex: "#E5E7EB", token: "border", text: "text-navy", usage: "Hairlines, card borders, dividers" },
  { name: "Destructive", hex: "#EF4444", token: "destructive", text: "text-white", usage: "Delete confirmations, errors" },
];

const STATUS_TOKENS = [
  ["New", "#3B82F6"], ["Active", "#10B981"], ["Pending", "#F59E0B"],
  ["Won", "#10B981"], ["Lost", "#EF4444"], ["Closed", "#6B7280"],
];

const SPEC = {
  "03": [
    ["Route", "/, /attorney, /practice-areas, /blog, /blog/:slug, /contact, /book-consultation"],
    ["Shell", "Sticky navbar (white/90 backdrop-blur, h-20) + dark navy footer (4 columns)"],
    ["Hero", "Navy bg + grain overlay, split 2-col, serif H1 5xl→7xl, gold kicker line, lawyer photo with offset gold frame"],
    ["Sections", "Stats bar → Practice grid (3 col) → About (asymmetric overlap images) → Awards (navy, hairline grid) → Testimonials (2 col) → Featured blogs (3 col) → CTA banner"],
    ["Buttons", "Gold solid (primary CTA), navy solid, navy outline — all square corners, uppercase, tracking-wider"],
  ],
  "05": [
    ["Layout", "260px fixed navy sidebar + white sticky topbar (h-16) + ivory content (max-w-1400, p-8)"],
    ["Sidebar", "Gold logo mark, ivory/60 links, active = gold text + 2px gold left border + ivory/5 fill"],
    ["Topbar", "Breadcrumbs left, notification bell (gold unread badge) + avatar dropdown right"],
    ["Overview", "4 stat cards → Area chart (clients/mo, gold) + Bar chart (cases/status) → 3-column lists (appointments, clients, activity)"],
    ["Cards", "White, border #E5E7EB, rounded-sm, shadow-card; hover lifts to shadow-hovercard"],
  ],
  "06": [
    ["List", "Toolbar: debounced search (300ms) + status select; table 6 cols; 10/page pagination"],
    ["Row", "Navy avatar square with gold initials, name, email+phone, case type, status badge, since-date, edit/delete icon actions"],
    ["Create/Edit", "Modal (max-w-2xl), 2-col grid: name*, email, phone, DOB, address (full), case type, status"],
    ["Delete", "AlertDialog: warns that cases, appointments, and documents are also removed"],
    ["States", "Skeleton rows while loading, dashed EmptyState with CTA, toast on save/delete"],
  ],
  "07": [
    ["Header", "Large navy avatar, name + status badge, contact row with gold icons, Edit button"],
    ["Tabs", "Underline tabs: Overview | Cases | Documents | Appointments | Notes | Activity — gold active underline"],
    ["Overview", "3 cards: details DL, matters counts (cases/docs/appts), latest note"],
    ["Notes", "Inline composer + reverse-chron list, delete with confirm"],
    ["Activity", "Gold-dot vertical timeline from audit feed"],
  ],
  "08": [
    ["Banner", "Gold-100 privilege notice with lock icon — confidentiality always visible"],
    ["Upload", "Dashed dropzone (drag-over → gold fill), click-to-browse, client + category selects, per-file progress bar (gold), 15MB cap"],
    ["Grid", "3-col cards: icon well, name, client, category chip, size, date; hover reveals Preview / Download / Delete"],
    ["Preview", "Inline browser preview for images, PDFs, and text via ?inline=true"],
    ["States", "Upload progress, error bar (red), success toast, dashed empty state, skeleton grid"],
  ],
  "09": [
    ["Filters", "Status pill row (All + 6 statuses) + debounced search across title/number/client"],
    ["Table", "Case title + mono case number, client, area, status badge, filed date, next hearing (gold), row click → edit modal"],
    ["Modal", "2-col form: title*, client*, area, status, filed date, next hearing, description, internal notes"],
    ["Status flow", "New → Active → Pending → Won/Lost → Closed; status changes write to activity feed"],
  ],
  "10": [
    ["Views", "Month (7-col grid, 2 chips + overflow count), Week (7 day columns), Day (hourly 8AM–7PM), List (upcoming 30)"],
    ["Navigation", "Prev / Today / Next + serif period title; view switcher segmented control"],
    ["Today", "Gold filled day number; other-month days muted at 40%"],
    ["Create/Edit", "Click any day cell → modal prefilled with date; client*, date*, time*, duration, type, location, status, notes"],
    ["Cancel", "Confirm dialog → status flips to Cancelled (strikethrough chip), activity entry written"],
  ],
  "11": [
    ["Admin list", "Rows: cover thumb, title + status badge, category · read time · date; publish toggle / edit / delete actions"],
    ["Editor", "2-col: main (title, excerpt, rich-text via Quill) + SEO block with live SERP preview; sidebar (slug, category, tags, cover URL + preview)"],
    ["Publish", "Navy action card: gold Publish + outline Save Draft; publish stamps published_at and logs activity"],
    ["Public", "Blog grid (featured + 3-col), category filters + search, detail with serif drop quote, tags, related posts, CTA banner"],
  ],
  "12": [
    ["Profile form", "3-col: basic info + credentials (dynamic list editors) left; photo, contact, stats, social right"],
    ["Live link", "PUT /api/profile powers every public page — save toast confirms site update"],
    ["Settings", "Account DL, change password (current + new + confirm, 8+ chars), preference switches"],
  ],
};

const FLOWS = [
  { name: "Visitor books a consultation", steps: ["Public site → Book Consultation", "Validates form client-side", "POST /api/public/inquiries", "Success panel + toast", "Notification created for attorney", "Appears in Dashboard → Notifications → Inquiries"] },
  { name: "Attorney onboards a client", steps: ["Dashboard → Clients → Add Client", "Modal form validates", "Client saved, activity logged", "Open case from Cases → select client", "Upload documents against client/case", "Schedule appointment on calendar"] },
  { name: "Publishing an insight", steps: ["Blog → New Post", "Write in rich text editor", "Set category, tags, SEO fields", "Publish → live on /blog instantly", "Featured on homepage (latest 3)"] },
  { name: "Managing a case lifecycle", steps: ["Case opened with auto number", "Status moves New → Active → Pending", "Hearings tracked via next_hearing", "Status → Won/Lost/Closed", "Every change lands in activity feed"] },
  { name: "Document handling", steps: ["Drag files onto dropzone", "Assign client + category", "Progress bar per file", "Preview inline or download", "Delete requires confirmation"] },
  { name: "Profile edits go live", steps: ["Dashboard → Profile", "Edit bio, stats, awards, photo", "Save → public site re-renders", "Footer, hero, about all update"] },
];

function SectionHeading({ num, title, desc }) {
  return (
    <div className="mb-10">
      <p className="text-xs font-semibold uppercase tracking-[0.3em] text-gold-700 mb-2">{num}</p>
      <h2 className="font-serif text-4xl sm:text-5xl font-semibold text-navy tracking-tight">{title}</h2>
      {desc && <p className="text-muted-foreground mt-3 max-w-2xl text-sm leading-relaxed">{desc}</p>}
    </div>
  );
}

function Swatch({ name, hex, token, text, usage }) {
  return (
    <div className="border border-border bg-white" data-testid={`swatch-${token}`}>
      <div className={`h-20 flex items-end p-3 ${text}`} style={{ background: hex }}>
        <span className="text-xs font-bold">{hex}</span>
      </div>
      <div className="p-4">
        <p className="text-sm font-semibold text-navy">{name}</p>
        <p className="text-xs font-mono text-gold-700 mt-0.5">{token}</p>
        <p className="text-xs text-muted-foreground mt-1.5">{usage}</p>
      </div>
    </div>
  );
}

function SpecTable({ rows }) {
  return (
    <div className="bg-white border border-border divide-y divide-border">
      {rows.map(([k, v]) => (
        <div key={k} className="grid sm:grid-cols-[200px_1fr] gap-1 sm:gap-6 px-5 py-4">
          <p className="text-xs font-semibold uppercase tracking-widest text-gold-700 pt-0.5">{k}</p>
          <p className="text-sm text-foreground leading-relaxed">{v}</p>
        </div>
      ))}
    </div>
  );
}

export default function DesignSystem() {
  const [active, setActive] = useState("tokens");
  const [navOpen, setNavOpen] = useState(false);

  const go = (id) => {
    setActive(id);
    setNavOpen(false);
    window.scrollTo({ top: 0 });
  };

  return (
    <div className="min-h-screen bg-ivory flex" data-testid="design-system-page">
      <aside className="hidden lg:block w-[280px] shrink-0 bg-navy fixed inset-y-0 left-0 z-40 overflow-y-auto" data-testid="ds-sidebar">
        <div className="px-6 h-20 flex items-center gap-3 border-b border-ivory/10">
          <span className="h-9 w-9 bg-gold flex items-center justify-center"><Scale className="h-4 w-4 text-navy" /></span>
          <div>
            <p className="font-serif font-semibold text-ivory leading-tight">Design Handoff</p>
            <p className="text-[10px] uppercase tracking-widest text-ivory/40">Wanar & Associates</p>
          </div>
        </div>
        <nav className="py-4 px-3">
          {SECTIONS.map((s) => (
            <button key={s.id} onClick={() => go(s.id)} data-testid={`ds-nav-${s.id}`}
              className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm transition-colors border-l-2 ${active === s.id ? "border-gold text-gold bg-ivory/5" : "border-transparent text-ivory/60 hover:text-ivory"}`}>
              <span className="font-mono text-[10px] w-6">{s.num}</span> {s.label}
            </button>
          ))}
        </nav>
        <div className="px-6 pb-6 space-y-2">
          <Link to="/" className="block text-xs text-ivory/50 hover:text-gold transition-colors" data-testid="ds-back-site">← Public website</Link>
          <Link to="/dashboard" className="block text-xs text-ivory/50 hover:text-gold transition-colors" data-testid="ds-back-dashboard">← Dashboard</Link>
        </div>
      </aside>

      <div className="lg:hidden fixed top-0 inset-x-0 z-50 bg-navy h-14 flex items-center justify-between px-4">
        <span className="font-serif font-semibold text-ivory">Design Handoff</span>
        <button onClick={() => setNavOpen(!navOpen)} className="text-ivory p-2" data-testid="ds-mobile-menu" aria-label="Toggle sections">
          {navOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>
      {navOpen && (
        <div className="lg:hidden fixed inset-x-0 top-14 z-40 bg-navy border-t border-ivory/10 max-h-[70vh] overflow-y-auto" data-testid="ds-mobile-nav">
          {SECTIONS.map((s) => (
            <button key={s.id} onClick={() => go(s.id)} data-testid={`ds-mobile-nav-${s.id}`}
              className={`w-full flex items-center gap-3 px-6 py-3 text-left text-sm border-b border-ivory/5 ${active === s.id ? "text-gold" : "text-ivory/70"}`}>
              <span className="font-mono text-[10px] w-6">{s.num}</span> {s.label}
            </button>
          ))}
        </div>
      )}

      <main className="flex-1 lg:ml-[280px] p-6 sm:p-10 lg:p-14 pt-24 lg:pt-14 max-w-6xl">

        {active === "tokens" && (
          <section data-testid="ds-section-tokens">
            <SectionHeading num="01 — Foundations" title="Design System"
              desc="Old-money legal: deep navy, warm ivory, restrained gold. Sharp corners, serif display type, generous whitespace. Nothing rounded, nothing playful." />
            <h3 className="text-xs font-semibold uppercase tracking-widest text-navy mb-4">Color Tokens</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
              {COLORS.map((c) => <Swatch key={c.token} {...c} />)}
            </div>

            <h3 className="text-xs font-semibold uppercase tracking-widest text-navy mb-4">Status Colors</h3>
            <div className="flex flex-wrap gap-3 mb-12">
              {STATUS_TOKENS.map(([s, hex]) => (
                <div key={s} className="flex items-center gap-2 bg-white border border-border px-3 py-2">
                  <span className="h-3 w-3" style={{ background: hex }} />
                  <span className="text-xs font-semibold text-navy">{s}</span>
                  <span className="text-[10px] font-mono text-muted-foreground">{hex}</span>
                </div>
              ))}
            </div>

            <h3 className="text-xs font-semibold uppercase tracking-widest text-navy mb-4">Typography</h3>
            <div className="bg-white border border-border divide-y divide-border mb-12">
              <div className="p-6"><p className="font-serif text-5xl font-semibold text-navy tracking-tight">Display / H1 — Cormorant Garamond 600, 48–72px</p></div>
              <div className="p-6"><p className="font-serif text-4xl font-semibold text-navy tracking-tight">H2 — 36–48px, section headings</p></div>
              <div className="p-6"><p className="font-serif text-2xl font-semibold text-navy">H3 — 24–30px, card titles</p></div>
              <div className="p-6"><p className="text-base text-muted-foreground leading-relaxed">Body — Manrope 400, 16px, line-height 1.65. Used for all paragraphs, table cells, and form content. Never center-aligned.</p></div>
              <div className="p-6"><p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold-700">Kicker / Label — Manrope 700, 12px, uppercase, wide tracking</p></div>
              <div className="p-6"><p className="font-mono text-sm text-navy">Mono — case numbers, tokens: 2026-CV-0101</p></div>
            </div>

            <div className="grid sm:grid-cols-3 gap-4 mb-12">
              <div className="bg-white border border-border p-6">
                <h3 className="text-xs font-semibold uppercase tracking-widest text-navy mb-4">Spacing</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">Sections: py-24 → py-32. Container: max-w-7xl, px-6/8. Cards: p-6 → p-10. Grids: gap-5/6. Sidebar: 260/280px.</p>
              </div>
              <div className="bg-white border border-border p-6">
                <h3 className="text-xs font-semibold uppercase tracking-widest text-navy mb-4">Radius & Elevation</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">Radius: 0–2px everywhere (sharp = authority). Shadows: card 8/30/-4 navy 8%; hover 20/40/-4 navy 12%. No colored shadows.</p>
              </div>
              <div className="bg-white border border-border p-6">
                <h3 className="text-xs font-semibold uppercase tracking-widest text-navy mb-4">Iconography & Motion</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">Lucide, 16–20px, sharp geometric set. Motion: 300–700ms cubic-bezier(0.16,1,0.3,1), fade+rise 24px, stagger 80ms.</p>
              </div>
            </div>
          </section>
        )}

        {active === "components" && (
          <section data-testid="ds-section-components">
            <SectionHeading num="02 — Library" title="Components" desc="Live, interactive specimens of every reusable component and its states." />

            <h3 className="text-xs font-semibold uppercase tracking-widest text-navy mb-4">Buttons</h3>
            <div className="bg-white border border-border p-6 flex flex-wrap items-center gap-4 mb-10" data-testid="ds-buttons">
              <button className="bg-gold text-navy hover:bg-gold-600 transition-colors duration-300 font-semibold uppercase tracking-wider text-xs px-7 py-3.5">Primary Gold</button>
              <button className="bg-navy text-ivory hover:bg-navy-800 transition-colors duration-300 font-semibold uppercase tracking-wider text-xs px-7 py-3.5">Secondary Navy</button>
              <button className="border-2 border-navy text-navy hover:bg-navy hover:text-ivory transition-colors duration-300 font-semibold uppercase tracking-wider text-xs px-7 py-3.5">Outline</button>
              <button className="bg-red-600 text-white hover:bg-red-700 transition-colors font-semibold uppercase tracking-wider text-xs px-7 py-3.5 inline-flex items-center gap-2"><Trash2 className="h-3.5 w-3.5" /> Destructive</button>
              <button disabled className="bg-navy text-ivory font-semibold uppercase tracking-wider text-xs px-7 py-3.5 opacity-40 cursor-not-allowed">Disabled</button>
              <button className="bg-navy text-ivory font-semibold uppercase tracking-wider text-xs px-7 py-3.5 inline-flex items-center gap-2"><span className="h-3.5 w-3.5 border-2 border-ivory/40 border-t-ivory rounded-full animate-spin" /> Loading</button>
            </div>

            <h3 className="text-xs font-semibold uppercase tracking-widest text-navy mb-4">Inputs</h3>
            <div className="bg-white border border-border p-6 grid sm:grid-cols-2 gap-6 mb-10" data-testid="ds-inputs">
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-navy-700">Public form (underline)</label>
                <input placeholder="Jane A. Smith" className="mt-1.5 bg-transparent border-b-2 border-border focus:border-gold py-3 w-full outline-none transition-colors text-sm" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-navy-700">Dashboard form (boxed)</label>
                <input placeholder="Search clients…" className="mt-1.5 bg-white border border-border focus:border-gold px-3 py-2.5 w-full outline-none transition text-sm" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-navy-700">With icon</label>
                <div className="relative mt-1.5">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input placeholder="Search…" className="bg-white border border-border focus:border-gold px-3 py-2.5 pl-10 w-full outline-none transition text-sm" />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-navy-700">Error state</label>
                <input defaultValue="not-an-email" className="mt-1.5 bg-white border border-red-400 px-3 py-2.5 w-full outline-none text-sm" />
                <p className="text-xs text-red-600 mt-1">Enter a valid email</p>
              </div>
            </div>

            <h3 className="text-xs font-semibold uppercase tracking-widest text-navy mb-4">Badges & Avatars</h3>
            <div className="bg-white border border-border p-6 flex flex-wrap items-center gap-4 mb-10" data-testid="ds-badges">
              {["New", "Active", "Pending", "Won", "Lost", "Closed", "Scheduled", "Cancelled", "draft", "published"].map((s) => <StatusBadge key={s} status={s} />)}
              <span className="w-px h-6 bg-border mx-2" />
              <Avatar className="rounded-none"><AvatarFallback className="rounded-none bg-navy text-gold font-serif">SW</AvatarFallback></Avatar>
              <Avatar className="rounded-none"><AvatarFallback className="rounded-none bg-gold-100 text-gold-700 font-serif">HB</AvatarFallback></Avatar>
              <Avatar className="rounded-none h-12 w-12"><AvatarFallback className="rounded-none bg-navy text-gold font-serif text-lg">MR</AvatarFallback></Avatar>
            </div>

            <h3 className="text-xs font-semibold uppercase tracking-widest text-navy mb-4">Cards, Tabs, Modal, Alerts</h3>
            <div className="bg-white border border-border p-6 space-y-8 mb-10" data-testid="ds-cards-tabs">
              <div className="grid sm:grid-cols-3 gap-4">
                <div className="border border-border p-5 shadow-card"><p className="font-serif text-lg font-semibold text-navy">Card</p><p className="text-xs text-muted-foreground mt-1">Default — shadow-card, sharp corners.</p></div>
                <div className="border border-gold p-5 shadow-hovercard"><p className="font-serif text-lg font-semibold text-navy">Card / Hover</p><p className="text-xs text-muted-foreground mt-1">Gold border + lifted shadow.</p></div>
                <div className="bg-navy p-5"><p className="font-serif text-lg font-semibold text-ivory">Card / Dark</p><p className="text-xs text-ivory/60 mt-1">Used for CTAs and quick actions.</p></div>
              </div>
              <Tabs defaultValue="one">
                <TabsList className="bg-transparent border-b border-border rounded-none w-full justify-start h-auto p-0 gap-1">
                  {["one", "two", "three"].map((t, i) => (
                    <TabsTrigger key={t} value={t} className="rounded-none border-b-2 border-transparent data-[state=active]:border-gold data-[state=active]:text-navy data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Tab {i + 1}
                    </TabsTrigger>
                  ))}
                </TabsList>
                <TabsContent value="one" className="pt-4 text-sm text-muted-foreground">Underline tabs — active state is a 2px gold underline with navy text.</TabsContent>
                <TabsContent value="two" className="pt-4 text-sm text-muted-foreground">Second tab content.</TabsContent>
                <TabsContent value="three" className="pt-4 text-sm text-muted-foreground">Third tab content.</TabsContent>
              </Tabs>
              <div className="flex flex-wrap gap-4">
                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="rounded-none" data-testid="ds-open-modal"><MousePointerClick className="h-4 w-4 mr-2" /> Open Modal</Button>
                  </DialogTrigger>
                  <DialogContent data-testid="ds-modal">
                    <DialogHeader><DialogTitle className="font-serif text-2xl text-navy">Modal specimen</DialogTitle></DialogHeader>
                    <p className="text-sm text-muted-foreground">Radix Dialog — max-w-xl/2xl, sharp corners, serif title. Destructive flows use AlertDialog with a red confirm.</p>
                  </DialogContent>
                </Dialog>
                <Button variant="outline" className="rounded-none" onClick={() => toast.success("Saved successfully")} data-testid="ds-toast-success">Toast / Success</Button>
                <Button variant="outline" className="rounded-none" onClick={() => toast.error("Something went wrong")} data-testid="ds-toast-error">Toast / Error</Button>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Alert className="rounded-none border-l-4 border-l-gold bg-gold-100/50"><Lock className="h-4 w-4" /><AlertTitle>Confidential</AlertTitle><AlertDescription>Gold informational banner — privilege notices.</AlertDescription></Alert>
                <Alert variant="destructive" className="rounded-none border-l-4"><Bell className="h-4 w-4" /><AlertTitle>Error</AlertTitle><AlertDescription>Red banner for blocking errors.</AlertDescription></Alert>
              </div>
            </div>

            <h3 className="text-xs font-semibold uppercase tracking-widest text-navy mb-4">Table, Pagination, Upload, Progress, Skeletons</h3>
            <div className="bg-white border border-border p-6 space-y-8 mb-10" data-testid="ds-data-components">
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[520px]">
                  <thead><tr className="border-b border-border text-left">{["Client", "Matter", "Status"].map((h) => <th key={h} className="px-4 py-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground">{h}</th>)}</tr></thead>
                  <tbody className="divide-y divide-border">
                    <tr className="hover:bg-muted/50 transition-colors"><td className="px-4 py-3 font-semibold text-navy">Harold Bennett</td><td className="px-4 py-3 text-muted-foreground">Bennett v. Sterling Logistics</td><td className="px-4 py-3"><StatusBadge status="Active" /></td></tr>
                    <tr className="hover:bg-muted/50 transition-colors"><td className="px-4 py-3 font-semibold text-navy">Priya Raman</td><td className="px-4 py-3 text-muted-foreground">Raman v. Raman</td><td className="px-4 py-3"><StatusBadge status="Pending" /></td></tr>
                  </tbody>
                </table>
              </div>
              <PaginationBar page={2} pages={4} total={38} onChange={() => {}} />
              <div className="border-2 border-dashed border-border p-8 text-center">
                <UploadCloud className="h-8 w-8 mx-auto mb-3 text-muted-foreground" />
                <p className="font-serif text-lg font-semibold text-navy">File upload dropzone</p>
                <p className="text-xs text-muted-foreground mt-1">Drag-over → gold fill. Per-file progress below.</p>
                <div className="max-w-xs mx-auto mt-4 space-y-2">
                  <Progress value={72} className="h-1 rounded-none" />
                  <p className="text-[10px] uppercase tracking-widest text-gold-700 font-semibold text-right">Retainer.pdf — 72%</p>
                </div>
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                <Skeleton className="h-24 rounded-none" />
                <Skeleton className="h-24 rounded-none" />
                <Skeleton className="h-24 rounded-none" />
              </div>
              <EmptyState icon={FileText} title="Empty state" description="Dashed border, gold icon well, serif title, optional CTA." />
              <div className="flex items-center gap-4"><Switch defaultChecked /><span className="text-sm text-muted-foreground">Switch — preferences</span><CheckCircle2 className="h-5 w-5 text-emerald-600" /><span className="text-sm text-muted-foreground">Success indicator</span></div>
            </div>
          </section>
        )}

        {["03", "05", "06", "07", "08", "09", "10", "11", "12"].includes(SECTIONS.find((s) => s.id === active)?.num) && (
          <section data-testid={`ds-section-${active}`}>
            <SectionHeading num={`${SECTIONS.find((s) => s.id === active).num} — Blueprint`}
              title={SECTIONS.find((s) => s.id === active).label}
              desc="Implementation spec for the React developer — structure, states, and interactions. This page is the contract; the live app matches it." />
            <SpecTable rows={SPEC[SECTIONS.find((s) => s.id === active).num]} />
            <div className="mt-8 flex gap-3">
              <Link to={["03"].includes(SECTIONS.find((s) => s.id === active).num) ? "/" : "/dashboard"}
                className="inline-flex items-center gap-2 bg-navy text-ivory hover:bg-navy-800 transition-colors font-semibold uppercase tracking-wider text-xs px-6 py-3" data-testid="ds-view-live">
                View live <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </section>
        )}

        {(active === "responsive-site" || active === "responsive-dash") && (
          <section data-testid={`ds-section-${active}`}>
            <SectionHeading num={active === "responsive-site" ? "04 — Breakpoints" : "13 — Breakpoints"}
              title={active === "responsive-site" ? "Responsive Website" : "Responsive Dashboard"}
              desc="Three canonical widths. Desktop-first layouts collapse along the same rules everywhere." />
            <div className="space-y-6">
              {[
                { w: "1440px", name: "Desktop", icon: "▭", detail: active === "responsive-site"
                  ? "7xl container, hero split 2-col, practice grid 3-col, about overlap visible, footer 4-col."
                  : "Fixed 260px sidebar, topbar breadcrumbs visible, stats 4-col, charts 3/2 split, tables full width." },
                { w: "768px", name: "Tablet", icon: "▯", detail: active === "responsive-site"
                  ? "Grids collapse to 2-col, hero stacks (photo hidden below lg), footer 2-col, nav still inline at lg."
                  : "Sidebar hidden → hamburger drawer overlay, stats 2-col, chart grid stacks, tables scroll horizontally." },
                { w: "375/390px", name: "Mobile", icon: "▯", detail: active === "responsive-site"
                  ? "Hamburger menu with full-width drawer, single column, CTAs full-width, stats 2-col grid, generous tap targets (44px+)."
                  : "Drawer nav, topbar shows page title instead of breadcrumbs, single-column everything, card grids stack, calendar month cells compact (92px)." },
              ].map((bp) => (
                <div key={bp.w} className="bg-white border border-border p-6 flex flex-col sm:flex-row gap-6" data-testid={`ds-bp-${bp.name.toLowerCase()}`}>
                  <div className="sm:w-48 shrink-0">
                    <p className="font-serif text-2xl font-semibold text-navy">{bp.name}</p>
                    <p className="text-xs font-mono text-gold-700 mt-1">{bp.w}</p>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">{bp.detail}</p>
                </div>
              ))}
              <div className="bg-navy text-ivory p-6 text-sm leading-relaxed">
                <p className="text-xs font-semibold uppercase tracking-widest text-gold mb-2">Rules</p>
                Breakpoints: sm 640 / md 768 / lg 1024 / xl 1280. Touch targets ≥ 44px. Horizontal scroll (never clip) for data tables. Sticky bars stay sticky at every width.
              </div>
            </div>
          </section>
        )}

        {active === "flows" && (
          <section data-testid="ds-section-flows">
            <SectionHeading num="14 — Journeys" title="User Flows" desc="The six core journeys, step by step — each is fully implemented in the live app." />
            <div className="space-y-5">
              {FLOWS.map((f, fi) => (
                <div key={f.name} className="bg-white border border-border p-6" data-testid={`ds-flow-${fi}`}>
                  <h3 className="font-serif text-xl font-semibold text-navy mb-4">{f.name}</h3>
                  <div className="flex flex-wrap items-center gap-2">
                    {f.steps.map((s, i) => (
                      <span key={i} className="flex items-center gap-2">
                        <span className="bg-gold-100 border border-gold/40 text-navy text-xs font-medium px-3 py-1.5">{s}</span>
                        {i < f.steps.length - 1 && <ArrowRight className="h-3.5 w-3.5 text-gold-700" />}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

      </main>
    </div>
  );
}
